<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Modal from 'dashboard/components/Modal.vue';
import ButtonV4 from 'next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const STATUS_COLORS = {
  approved: 'text-n-teal-11 bg-n-teal-2 border-n-teal-7',
  pending: 'text-n-amber-11 bg-n-amber-2 border-n-amber-7',
  rejected: 'text-n-ruby-11 bg-n-ruby-2 border-n-ruby-7',
  paused: 'text-n-slate-11 bg-n-slate-2 border-n-slate-7',
  disabled: 'text-n-slate-11 bg-n-slate-2 border-n-slate-7',
  deleted: 'text-n-slate-9 bg-n-slate-2 border-n-slate-7',
};

const CATEGORY_ICONS = {
  marketing: 'i-lucide-megaphone',
  utility: 'i-lucide-wrench',
  authentication: 'i-lucide-shield-check',
};

const getStatusColor = status => {
  return (
    STATUS_COLORS[status] || 'text-n-slate-11 bg-n-slate-2 border-n-slate-7'
  );
};

const getCategoryIcon = category => {
  return CATEGORY_ICONS[category] || 'i-lucide-file-text';
};

const templateComponents = computed(() => {
  if (!props.template.components) return {};

  const components = {};

  if (Array.isArray(props.template.components)) {
    props.template.components.forEach(comp => {
      if (comp.type === 'HEADER') {
        components.header = comp.text || comp.url;
      } else if (comp.type === 'BODY') {
        components.body = comp.text;
      } else if (comp.type === 'FOOTER') {
        components.footer = comp.text;
      } else if (comp.type === 'BUTTONS') {
        components.buttons = comp.buttons || [];
      }
    });
  }

  return components;
});

const formatDate = dateString => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Modal :show="true" :on-close="handleClose">
    <div class="flex flex-col w-full h-auto max-w-3xl">
      <!-- Header -->
      <div
        class="flex justify-between items-center p-6 border-b border-n-weak bg-n-solid-1"
      >
        <div class="flex gap-3 items-center flex-1 min-w-0">
          <div
            class="flex-shrink-0 flex justify-center items-center w-10 h-10 rounded-lg bg-n-alpha-2"
          >
            <Icon
              :icon="getCategoryIcon(template.category)"
              class="w-5 h-5 text-n-slate-11"
            />
          </div>
          <div class="flex-1 min-w-0">
            <h2
              class="text-lg font-semibold text-n-slate-12 truncate font-mono"
            >
              {{ template.name }}
            </h2>
            <p class="text-xs text-n-slate-11">
              {{ t('INBOX_MGMT.TEMPLATES.PREVIEW.SUBTITLE') }}
            </p>
          </div>
          <span
            class="flex-shrink-0 inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border"
            :class="getStatusColor(template.status)"
          >
            {{
              t(`INBOX_MGMT.TEMPLATES.STATUS.${template.status.toUpperCase()}`)
            }}
          </span>
        </div>
        <button
          class="flex-shrink-0 ml-3 p-1.5 rounded-lg hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 transition-colors"
          @click="handleClose"
        >
          <Icon icon="i-lucide-x" class="w-5 h-5" />
        </button>
      </div>

      <!-- Content -->
      <div
        class="flex-1 p-6 space-y-6 overflow-y-auto max-h-[70vh] bg-n-solid-2"
      >
        <!-- Template Info -->
        <div class="p-4 rounded-lg bg-n-solid-1 border border-n-weak">
          <h3
            class="flex gap-2 items-center mb-3 text-sm font-semibold text-n-slate-12"
          >
            <Icon icon="i-lucide-info" class="w-4 h-4" />
            {{ t('INBOX_MGMT.TEMPLATES.PREVIEW.INFO_TITLE') }}
          </h3>
          <div class="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span class="block mb-1 text-n-slate-11">
                {{ t('INBOX_MGMT.TEMPLATES.CATEGORY_LABEL') }}
              </span>
              <span
                class="flex gap-1.5 items-center font-medium text-n-slate-12"
              >
                <Icon icon="i-lucide-tag" class="w-3.5 h-3.5" />
                {{
                  t(
                    `INBOX_MGMT.TEMPLATES.CATEGORIES.${template.category.toUpperCase()}`
                  )
                }}
              </span>
            </div>
            <div>
              <span class="block mb-1 text-n-slate-11">
                {{ t('INBOX_MGMT.TEMPLATES.LANGUAGE_LABEL') }}
              </span>
              <span
                class="flex gap-1.5 items-center font-medium text-n-slate-12"
              >
                <Icon icon="i-lucide-globe" class="w-3.5 h-3.5" />
                {{ template.language }}
              </span>
            </div>
            <div>
              <span class="block mb-1 text-n-slate-11">
                {{ t('INBOX_MGMT.TEMPLATES.CREATED_AT_LABEL') }}
              </span>
              <span
                class="flex gap-1.5 items-center font-medium text-n-slate-12"
              >
                <Icon icon="i-lucide-calendar" class="w-3.5 h-3.5" />
                {{ formatDate(template.created_at) }}
              </span>
            </div>
            <div v-if="template.whatsapp_template_id">
              <span class="block mb-1 text-n-slate-11">Template ID</span>
              <span
                class="flex gap-1.5 items-center font-medium text-n-slate-12 font-mono text-[10px]"
              >
                {{ template.whatsapp_template_id }}
              </span>
            </div>
          </div>
        </div>

        <!-- Rejection Reason -->
        <div
          v-if="template.rejection_reason"
          class="p-4 rounded-lg bg-n-ruby-2 border border-n-ruby-7"
        >
          <div class="flex gap-3">
            <Icon
              icon="i-lucide-alert-circle"
              class="flex-shrink-0 w-5 h-5 text-n-ruby-11"
            />
            <div>
              <h3 class="mb-1 text-sm font-semibold text-n-ruby-12">
                {{ t('INBOX_MGMT.TEMPLATES.PREVIEW.REJECTION_TITLE') }}
              </h3>
              <p class="text-xs text-n-ruby-11">
                {{ template.rejection_reason }}
              </p>
            </div>
          </div>
        </div>

        <!-- Message Preview -->
        <div>
          <h3
            class="flex gap-2 items-center mb-3 text-sm font-semibold text-n-slate-12"
          >
            <Icon icon="i-lucide-eye" class="w-4 h-4" />
            {{ t('INBOX_MGMT.TEMPLATES.PREVIEW.MESSAGE_TITLE') }}
          </h3>

          <!-- WhatsApp-like Preview -->
          <div
            class="p-6 rounded-lg bg-[#e5ddd5] min-h-[200px] relative overflow-hidden"
          >
            <!-- WhatsApp pattern background -->
            <div
              class="absolute inset-0 opacity-10"
              style="
                background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0icGF0dGVybiIgeD0iMCIgeT0iMCIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxwYXRoIGQ9Ik0gMTAgMTAgTCA5MCA5MCBNIDkwIDEwIEwgMTAgOTAiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0idXJsKCNwYXR0ZXJuKSIvPjwvc3ZnPg==');
              "
            />

            <!-- Message Bubble -->
            <div class="relative max-w-md ml-auto">
              <div class="px-3 py-2 rounded-lg bg-[#dcf8c6] shadow-sm">
                <!-- Header -->
                <div v-if="templateComponents.header" class="mb-2">
                  <p class="text-sm font-semibold" style="color: #000000">
                    {{ templateComponents.header }}
                  </p>
                  <div
                    class="my-2 border-b"
                    style="border-color: rgba(0, 0, 0, 0.1)"
                  />
                </div>

                <!-- Body -->
                <div v-if="templateComponents.body" class="mb-1">
                  <p
                    class="text-sm whitespace-pre-wrap leading-relaxed"
                    style="color: #000000"
                  >
                    {{ templateComponents.body }}
                  </p>
                </div>

                <!-- Footer -->
                <div
                  v-if="templateComponents.footer"
                  class="mt-2 pt-2"
                  style="border-top: 1px solid rgba(0, 0, 0, 0.1)"
                >
                  <p class="text-xs italic" style="color: rgba(0, 0, 0, 0.6)">
                    {{ templateComponents.footer }}
                  </p>
                </div>

                <!-- Timestamp -->
                <div class="flex gap-1 justify-end items-center mt-1">
                  <span class="text-[10px]" style="color: rgba(0, 0, 0, 0.45)">
                    {{
                      new Date().toLocaleTimeString('pt-BR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    }}
                  </span>
                  <Icon
                    icon="i-lucide-check-check"
                    class="w-3 h-3"
                    style="color: #53bdeb"
                  />
                </div>
              </div>

              <!-- Buttons (if any) -->
              <div
                v-if="
                  templateComponents.buttons &&
                  templateComponents.buttons.length
                "
                class="mt-1 space-y-1"
              >
                <button
                  v-for="(button, index) in templateComponents.buttons"
                  :key="index"
                  class="w-full px-4 py-2.5 text-sm font-medium text-center rounded-md transition-all"
                  style="
                    background: linear-gradient(
                      to bottom,
                      #ffffff 0%,
                      #f7f7f7 100%
                    );
                    border: 1px solid #d1d1d1;
                    color: #00a5f4;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
                    text-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
                  "
                  @mouseover="
                    $event.target.style.background =
                      'linear-gradient(to bottom, #f7f7f7 0%, #efefef 100%)'
                  "
                  @mouseleave="
                    $event.target.style.background =
                      'linear-gradient(to bottom, #ffffff 0%, #f7f7f7 100%)'
                  "
                >
                  <Icon
                    v-if="button.type === 'URL'"
                    icon="i-lucide-external-link"
                    class="inline w-3.5 h-3.5 mr-1.5"
                    style="color: #00a5f4"
                  />
                  <Icon
                    v-else-if="button.type === 'PHONE_NUMBER'"
                    icon="i-lucide-phone"
                    class="inline w-3.5 h-3.5 mr-1.5"
                    style="color: #00a5f4"
                  />
                  <span style="color: #00a5f4">{{ button.text }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Variables Info -->
        <div
          v-if="
            templateComponents.body && templateComponents.body.includes('{{')
          "
          class="p-4 rounded-lg bg-n-amber-2 border border-n-amber-7"
        >
          <div class="flex gap-3">
            <Icon
              icon="i-lucide-code-2"
              class="flex-shrink-0 w-5 h-5 text-n-amber-11"
            />
            <div>
              <h3 class="mb-1 text-sm font-semibold text-n-amber-12">
                {{ t('INBOX_MGMT.TEMPLATES.PREVIEW.VARIABLES_TITLE') }}
              </h3>
              <p class="text-xs text-n-amber-11">
                {{ t('INBOX_MGMT.TEMPLATES.PREVIEW.VARIABLES_MESSAGE') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex gap-3 justify-end items-center p-6 border-t border-n-weak bg-n-solid-1"
      >
        <ButtonV4 ghost slate @click="handleClose">
          {{ t('INBOX_MGMT.TEMPLATES.PREVIEW.CLOSE') }}
        </ButtonV4>
      </div>
    </div>
  </Modal>
</template>
